import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Calendar,
  Checkbox,
  Dropdown,
  DropdownItem,
  getColor,
  Icons,
  InputArea,
  InputField,
  Radio,
  Toggle,
  Tooltip,
  Typography,
  useToast,
} from "fds";
import "./index.scss";
import utilsDocs from "../../shared/utils/docs";
import { Member, WorkgroupCreateInfo } from "../../types/workgroup";
import UserSearchField from "../UserSearchField";
import utilsCommon from "../../shared/utils/common";
import { DocInfo, PermissionPolicy, UploadFile } from "../../types/files";
import useCheckboxGroup from "../../shared/hooks/useCheckboxGroup";

import classNames from "classnames";
import { t } from "i18next";
import i18n from "../../locales/i18n";
import useDocumentStore from "../../redux/dispatcher/useDocumentStore";
import PolicyToolTip from "../PolicyToolTip";
import { getUserInfoFromLocalStorage } from "../../shared/utils/auth";
import { LoginDto } from "../../types/auth";
import { Trans } from "react-i18next";
import NameTag from "../NameTag";
import workspaceUtil from "../../shared/utils/workspaceUtil";
import { file_share_policy_type } from "../WorkgroupModal/WorkgroupInfoSettingTab";
import dayjs from "dayjs";
import { nanoid } from "@reduxjs/toolkit";
interface CreateNewWorkgroupPageProps {
  classes?: string;
  uid?: string;
  files?: UploadFile[];
  changeFiles?: (file: UploadFile[]) => void;
  workgroupInfo?: WorkgroupCreateInfo;
  addedUserList: any[];
  disableValues: string[];
  copyFileResp?: {
    files: DocInfo[];
    workgroupId?: string;
    message?: string;
  };
  changeExtraSize?: (height: number) => void;
  changeExtraOpenModalInfo: (type: string) => void;
  permissionList?: { targetValue: string; policy: PermissionPolicy }[];
  changeCopyFileResp?: (req: {
    files: DocInfo[];
    workgroupId?: string;
    message?: string;
  }) => void;
  changeIsNextButtonDisabled: (v: boolean) => void;
  changeWorkgroupInfo?: (info: WorkgroupCreateInfo) => void;
  changePermissionList?: (
    permission: { targetValue: string; policy: PermissionPolicy }[]
  ) => void;
  changeAddedUserList: (user: any) => void;
  changeTargetPermissionUserList?: (userIds: string[]) => void;
  modalType?:
    | "CREATE_NEW_WORKGROUP"
    | "ADD_NEW_MEMBER"
    | "SELECT_RECEIVER"
    | "CREATE_NEW_SUB_WORKGROUP";
  message?: string;
  changeMessage?: (msg: string) => void;
  clientSize?: { width: number; height: number };
  isClient?: boolean;
  createType?: string;
  isSendShareMail?: boolean;
  changeSendShareMail: (value: boolean) => void;
  myBoxFileShareLinkPolicy: {
    authType: string;
    urlAction: string;
    allowDays: number;
  };
  changeMyBoxFileShareLinkPolicy: (policy: {
    authType: string;
    urlAction: string;
    allowDays: number;
  }) => void;
}
const CreateNewWorkgroupPage: React.FC<CreateNewWorkgroupPageProps> = ({
  files,
  uid,
  changeFiles,
  classes,
  changeExtraSize,
  workgroupInfo,
  disableValues,
  addedUserList,
  createType,
  modalType = "CREATE_NEW_WORKGROUP",
  copyFileResp,
  permissionList,
  clientSize,
  message,
  isClient,
  isSendShareMail,
  changeCopyFileResp,
  changeExtraOpenModalInfo,
  changeMessage,
  changePermissionList,
  changeIsNextButtonDisabled,
  changeWorkgroupInfo,
  changeAddedUserList,
  changeTargetPermissionUserList,
  changeSendShareMail,
  myBoxFileShareLinkPolicy,
  changeMyBoxFileShareLinkPolicy,
}) => {
  const user = getUserInfoFromLocalStorage() as string | "";
  const loginDto: LoginDto = JSON.parse(user);
  const myId = loginDto?.userInfo?.userId ?? "";
  const toast = useToast();
  const { removeFileFromUid } = useDocumentStore();
  const checkboxGroup = useCheckboxGroup<string>();
  const [buttonClick, changeButtonClick] = useState(0);
  useEffect(() => {
    changeIsNextButtonDisabled(
      ["CREATE_NEW_SUB_WORKGROUP", "CREATE_NEW_WORKGROUP"].includes(modalType)
        ? !workgroupInfo?.workgroupName
        : addedUserList.length === 0
    );
  }, [workgroupInfo?.workgroupName, addedUserList]);

  useEffect(() => {
    if (modalType === "SELECT_RECEIVER" && changeExtraSize) {
      let returnHeight = 0;
      if (myBoxFileShareLinkPolicy.authType !== "LOGIN") {
        returnHeight += 84;
        if (myBoxFileShareLinkPolicy.allowDays >= 0) returnHeight += 48;
      }
      if (
        [...(files ?? []), ...(copyFileResp?.files ?? [])]?.filter(
          (item: UploadFile | DocInfo) => {
            if (
              ![
                ...new Set([
                  ...(loginDto?.serviceConfig?.revisionExtensions || []),
                  ...(loginDto?.serviceConfig?.manualRevisionExtensions || []),
                ]),
              ].includes(
                ("fileName" in item
                  ? item.fileName
                  : "contentName" in item
                  ? item.contentName
                  : ""
                )
                  ?.split(".")
                  ?.pop() ?? ""
              )
            ) {
              return true;
            }
            return false;
          }
        )?.length > 0
      )
        returnHeight += 21;
      if (!isSendShareMail) returnHeight -= 80;
      changeExtraSize(returnHeight);
    }
  }, [
    myBoxFileShareLinkPolicy,
    modalType,
    isSendShareMail,
    files,
    copyFileResp?.files,
  ]);
  const handleChangeWorkgroupInfo = (key: string, value: string | boolean) => {
    workgroupInfo &&
      changeWorkgroupInfo &&
      changeWorkgroupInfo({ ...workgroupInfo, [key]: value });
  };
  const renderFileList = (files: any) => {
    return (
      <Box classes="upload-fileList">
        {files.map((file: any) => (
          <Box classes="upload-file">
            <Box>
              {utilsDocs.renderDocIcon(file.fileName ?? file.contentName, 20)}
            </Box>
            <Tooltip message={file.fileName ?? file.contentName} maxWidth={400}>
              <Typography type="body4_rg" ellipsis classes="mg ml-4">
                {file.fileName ?? file.contentName}
              </Typography>
            </Tooltip>
            {files.length > 1 && (
              <Box
                alignItems="center"
                classes="mg ml-4 cursor pointer"
                onClick={() => {
                  if (uid) {
                    removeFileFromUid(uid, file.uuid, file.fileId);
                    changeFiles &&
                      changeFiles(
                        files.filter((item: any) => item.fileId !== file.fileId)
                      );
                  } else {
                    changeCopyFileResp &&
                      changeCopyFileResp({
                        ...copyFileResp,
                        files:
                          copyFileResp?.files?.filter(
                            (item) => item?.syncId !== file?.syncId
                          ) ?? [],
                      });
                  }
                }}
              >
                <Icons variant="line" label="closeLarge" size={12} />
              </Box>
            )}
          </Box>
        ))}
      </Box>
    );
  };
  return (
    <Box direction={"column"} classes="newWorkgroup">
      {["CREATE_NEW_WORKGROUP", "CREATE_NEW_SUB_WORKGROUP"].includes(
        modalType
      ) && (
        <Box direction="column" classes="mg mb-20">
          <Typography type="subtitle2">{t("common.workgroupName")}</Typography>
          <InputField
            placeholder={t("workgroup.newWorkgroupPlaceholder")}
            classes="mg mt-6"
            size={"md"}
            maxLength={120}
            value={workgroupInfo?.workgroupName}
            onChange={(e) =>
              handleChangeWorkgroupInfo("workgroupName", e.target.value)
            }
          />
        </Box>
      )}
      <Typography type="subtitle2">{t("common.searchUser")}</Typography>
      <Box classes="mg mt-6">
        <UserSearchField
          width={
            workspaceUtil.checkMySpace()
              ? "100%"
              : i18n.language === "ko"
              ? "calc(100% - 131px)"
              : "calc(100% - 139px)"
          }
          clientSize={clientSize}
          invisibleUserIds={[
            ...addedUserList.map((item) => item.userId),
            ...disableValues,
          ]}
          onClick={(user: Member) =>
            changeAddedUserList([...addedUserList, user])
          }
        />
        {!workspaceUtil.checkMySpace() && (
          <Button
            btnStyle="fill"
            type="secondary"
            label={t("common.chooseMember")}
            classes="mg ml-8"
            size={"lg"}
            onClick={() => changeExtraOpenModalInfo("SELECT_USER")}
            leadingIcon={<Icons variant="line" label="addPerson" />}
          />
        )}
      </Box>
      <Box classes="mg mt-20 mb-4" alignItems="center">
        <Typography type="subtitle2">
          {addedUserList?.length === 0 ? (
            <Typography type="subtitle2">
              {modalType === "SELECT_RECEIVER"
                ? t("sendingFile.recipients")
                : t("common.member")}
            </Typography>
          ) : checkboxGroup.selected.length === 0 &&
            modalType !== "SELECT_RECEIVER" ? (
            <Trans
              i18nKey={"common.memberWithCount"}
              values={{ count: addedUserList?.length ?? 0 }}
              components={{
                1: (
                  <Typography
                    type="subtitle2"
                    exactColor={getColor("COLOR_TEXT_BRAND")}
                  />
                ),
              }}
            />
          ) : checkboxGroup.selected.length === 0 &&
            modalType === "SELECT_RECEIVER" ? (
            <Trans
              i18nKey={"sendingFile.recipientsWithCount"}
              values={{ count: addedUserList?.length ?? 0 }}
              components={{
                1: (
                  <Typography
                    type="subtitle2"
                    exactColor={getColor("COLOR_TEXT_BRAND")}
                  />
                ),
              }}
            />
          ) : (
            <Trans
              i18nKey="common.chosenMember"
              values={{
                count:
                  modalType === "SELECT_RECEIVER"
                    ? checkboxGroup.selected?.length
                    : addedUserList?.length,
              }}
              components={{
                1: (
                  <Typography
                    type="subtitle2"
                    exactColor={getColor("COLOR_TEXT_BRAND")}
                  />
                ),
              }}
            />
          )}
        </Typography>
        {modalType === "SELECT_RECEIVER" && (
          <Box alignItems="center" classes="mg ml-auto">
            <Dropdown
              value={t("common.setAuth")}
              btnStyle="link"
              btnSize="lg"
              iconPos="left"
              placeholder={" "}
              buttonClick={buttonClick}
              classes="mg ml-auto"
              leadingIcon={<Icons variant="line" label="shield" isOn />}
              buttonWidth={i18n.language === "ko" ? 154 : 200}
              align={"right"}
              disabled={checkboxGroup.selected.length === 0}
            >
              {[
                { key: "NORMAL", label: t("sendingFile.basic") },
                { key: "CUSTOM", label: t("sendingFile.custom") },
              ].map((item) => (
                <DropdownItem
                  label={item.label}
                  checkColor={item.key === "NORMAL"}
                  checked={item.key === "NORMAL"}
                  type="check"
                  onClick={() => {
                    changeButtonClick(buttonClick + 1);
                    item.key === "NORMAL"
                      ? changePermissionList && changePermissionList([])
                      : changeTargetPermissionUserList &&
                        changeTargetPermissionUserList(checkboxGroup.selected);
                  }}
                  trailingIcon={
                    item.key === "CUSTOM" ? (
                      <Icons variant="line" label="setting" classes="mg ml-8" />
                    ) : undefined
                  }
                />
              ))}
            </Dropdown>
            <Button
              label={t("common.initAuth")}
              btnStyle="link"
              size="lg"
              onClick={() => {
                changePermissionList &&
                  changePermissionList(
                    permissionList?.filter(
                      (item) =>
                        !checkboxGroup.selected?.includes(item.targetValue)
                    ) ?? []
                  );
                toast.toastMsg(nanoid(), t("workgroup.authReset"), "success");
              }}
              state={
                checkboxGroup.selected.length === 0 ? "disable" : "default"
              }
              leadingIcon={<Icons variant="line" label="redoLeft" isOn />}
            />
          </Box>
        )}
      </Box>
      <Box classes="newWorkgroup-members" direction="column">
        {modalType === "SELECT_RECEIVER" && addedUserList.length > 0 && (
          <Box width={"100%"}>
            <Box classes="newWorkgroup-members--header">
              <Checkbox
                indeterminate={
                  checkboxGroup.selected.length > 0 &&
                  checkboxGroup.selected.length < addedUserList.length
                }
                check={checkboxGroup.selected.length === addedUserList.length}
                onClick={() =>
                  checkboxGroup.handleAllClick(
                    checkboxGroup.selected.length === addedUserList.length
                      ? []
                      : addedUserList.map((item) => item.userId)
                  )
                }
              />
              <Typography
                type="body3_rg"
                classes="mg ml-8"
                width={358}
                exactColor={getColor("COLOR_TEXT_SECONDARY")}
              >
                {t("common.idAndName")}
              </Typography>
              <Typography
                type="body3_rg"
                exactColor={getColor("COLOR_TEXT_SECONDARY")}
              >
                {t("common.permission")}
              </Typography>
            </Box>
          </Box>
        )}
        {addedUserList.length === 0 ? (
          <Box
            direction="column"
            alignItems="center"
            justifyContent="center"
            width={"100%"}
            height={"100%"}
          >
            <img
              src={require("../../shared/images/pages/File/noPeople.png")}
              alt={"thumbnail"}
              width={200}
              height={120}
            />
            <Typography
              type="body4_rg"
              exactColor={getColor("COLOR_TEXT_SECONDARY")}
              width={200}
              textAlign="center"
            >
              {modalType === "SELECT_RECEIVER"
                ? t("sendingFile.selectMember")
                : t("workgroup.selectMember")}
            </Typography>
          </Box>
        ) : (
          <Box
            direction="column"
            classes={classNames("newWorkgroup-members--list", modalType)}
          >
            {addedUserList.map((item) => {
              const isCustom =
                permissionList &&
                permissionList?.filter(
                  (item1) => item1.targetValue === item.userId
                )?.length > 0;
              return (
                <PolicyToolTip
                  sendingType={isCustom ? "ADVANCED" : "NORMAL"}
                  policy={{
                    authType: "LOGIN",
                    changeCode: "CREATE",
                    createUser: myId,
                    policyCode: null,
                    policyType: "DOCUMENT",
                    syncId: "",
                    targetType: "USER",
                    targetValue: item.userId,
                    uniqueId: null,
                    updateTime: 1,
                    updateUser: myId,
                    workgroupCode: "NONEWORKGROUUP",
                    policy: {
                      ...(permissionList?.filter(
                        (item1) => item1.targetValue === item.userId
                      )[0]?.policy ??
                        utilsCommon.getValueFromKeyinEcoCommonConfig(
                          "ecoCommonConfig"
                        ).noneWorkgroupPolicyDTO?.policy),
                    },
                  }}
                  targetViewCount={0}
                  showTooltip={modalType === "SELECT_RECEIVER"}
                >
                  <Box
                    classes={classNames("newWorkgroup-members--user", {
                      selected: checkboxGroup.isSelected(item.userId),
                    })}
                  >
                    {modalType === "SELECT_RECEIVER" && (
                      <Checkbox
                        check={checkboxGroup.isSelected(item.userId)}
                        onClick={() => checkboxGroup.handleClick(item.userId)}
                        classDiv="mg ml-10"
                      />
                    )}
                    <NameTag
                      user={
                        item.userType === 1
                          ? {
                              ...item,
                              companyName: loginDto.ecoCommonConfig.companyName,
                            }
                          : item
                      }
                      width={335}
                      height={"100%"}
                      showOnlyOne
                      classes="mg ml-8"
                      withoutUserId={false}
                    />
                    {modalType === "SELECT_RECEIVER" && (
                      <Dropdown
                        placeholder=" "
                        btnSize="lg"
                        buttonWidth={154}
                        align="right"
                        btnStyle="none"
                        buttonClick={buttonClick}
                        positionStrategy="fixed"
                        classes="mg ml-auto tooltip-disable selectReciever-dropdown"
                        value={
                          isCustom
                            ? t("sendingFile.custom")
                            : t("sendingFile.basic")
                        }
                      >
                        <DropdownItem
                          label={t("sendingFile.basic")}
                          checkColor={!isCustom}
                          checked={!isCustom}
                          type="check"
                          onClick={() => {
                            changePermissionList &&
                              changePermissionList(
                                permissionList?.filter(
                                  (item1) => item1.targetValue !== item.userId
                                ) ?? []
                              );
                            changeButtonClick(buttonClick + 1);
                          }}
                        />
                        <DropdownItem
                          label={t("sendingFile.custom")}
                          checkColor={isCustom}
                          checked={isCustom}
                          type="check"
                          trailingIcon={
                            <Icons
                              variant="line"
                              label="setting"
                              classes="mg ml-8"
                            />
                          }
                          onClick={() =>
                            changeTargetPermissionUserList &&
                            changeTargetPermissionUserList([item.userId])
                          }
                        />
                      </Dropdown>
                    )}
                    <Button
                      btnStyle="icon"
                      classes={
                        modalType === "SELECT_RECEIVER"
                          ? "mg mr-12"
                          : "mg ml-auto"
                      }
                      onClick={() => {
                        changeAddedUserList(
                          addedUserList.filter(
                            (item1) => item1.userId !== item.userId
                          )
                        );
                        checkboxGroup.isSelected(item.userId) &&
                          checkboxGroup.handleClick(item.userId);
                      }}
                      leadingIcon={<Icons variant="line" label="delete" />}
                    />
                  </Box>
                </PolicyToolTip>
              );
            })}
          </Box>
        )}
      </Box>
      {((files && files.length > 0) ||
        (copyFileResp?.files && copyFileResp?.files?.length > 0)) &&
        renderFileList(files ?? copyFileResp?.files)}
      {modalType === "SELECT_RECEIVER" &&
        [...(files ?? []), ...(copyFileResp?.files ?? [])]?.filter(
          (item: UploadFile | DocInfo) => {
            if (
              ![
                ...new Set([
                  ...(loginDto?.serviceConfig?.revisionExtensions || []),
                  ...(loginDto?.serviceConfig?.manualRevisionExtensions || []),
                ]),
              ].includes(
                ("fileName" in item
                  ? item.fileName
                  : "contentName" in item
                  ? item.contentName
                  : ""
                )
                  ?.split(".")
                  ?.pop() ?? ""
              )
            ) {
              return true;
            }
            return false;
          }
        )?.length > 0 && (
          <Typography
            classes="mg mt-2"
            type="body4_rg"
            exactColor={getColor("COLOR_TEXT_ERROR_LIGHT")}
          >
            {t("file.noWrappingFileInclude")}
          </Typography>
        )}
      {modalType === "SELECT_RECEIVER" &&
        createType !== "ADD_RECIPIENTS_SEND_FILE" &&
        !window.eco.isOnlyVerifiedLinkAllowed && (
          <Box direction="column" classes="mg mt-20">
            <Typography type="subtitle2" classes="mg mb-16">
              {t("file.link.linkType")}
            </Typography>
            <Box alignItems="flex-start">
              <Radio
                label={t("common.verified")}
                check={myBoxFileShareLinkPolicy.authType === "LOGIN"}
                onClick={() =>
                  changeMyBoxFileShareLinkPolicy({
                    ...myBoxFileShareLinkPolicy,
                    authType: "LOGIN",
                  })
                }
              />
              <Box
                direction="column"
                classes="mg ml-80"
                justifyContent="flex-start"
              >
                <Radio
                  label={t("common.unVerified")}
                  check={myBoxFileShareLinkPolicy.authType !== "LOGIN"}
                  style={{ justifyContent: "flex-start" }}
                  onClick={() =>
                    changeMyBoxFileShareLinkPolicy({
                      ...myBoxFileShareLinkPolicy,
                      authType: "NONE",
                    })
                  }
                />
                {myBoxFileShareLinkPolicy.authType === "NONE" && (
                  <Box classes="mg ml-28 mt-12" direction="column">
                    <Dropdown
                      placeholder=" "
                      value={
                        file_share_policy_type.filter(
                          (item) =>
                            item?.key === myBoxFileShareLinkPolicy?.urlAction
                        )[0]?.resource
                      }
                      width={240}
                      direction={
                        modalType === "SELECT_RECEIVER" && !isSendShareMail
                          ? "up"
                          : "down"
                      }
                      buttonClick={buttonClick}
                    >
                      {file_share_policy_type.map((item) => (
                        <DropdownItem
                          label={item.resource}
                          key={item?.key}
                          checked={
                            item?.key === myBoxFileShareLinkPolicy?.urlAction
                          }
                          checkColor={
                            item?.key === myBoxFileShareLinkPolicy?.urlAction
                          }
                          type="check"
                          onClick={() => {
                            changeMyBoxFileShareLinkPolicy({
                              ...myBoxFileShareLinkPolicy,
                              urlAction: item.key,
                            });
                            changeButtonClick(buttonClick + 1);
                          }}
                        />
                      ))}
                    </Dropdown>
                    <Box classes="mg mt-12" direction="column">
                      <Checkbox
                        label={t("file.setEndDate")}
                        justifyContent="flex-start"
                        check={
                          !myBoxFileShareLinkPolicy?.allowDays ||
                          myBoxFileShareLinkPolicy?.allowDays > -1
                        }
                        onClick={() => {
                          changeMyBoxFileShareLinkPolicy({
                            ...myBoxFileShareLinkPolicy,
                            allowDays:
                              myBoxFileShareLinkPolicy?.allowDays === -1
                                ? 60
                                : -1,
                          });
                          changeButtonClick(buttonClick + 1);
                        }}
                      />
                      {myBoxFileShareLinkPolicy?.allowDays > -1 && (
                        <Calendar
                          type={"single"}
                          width={200}
                          selected={
                            new Date(
                              dayjs()
                                .add(myBoxFileShareLinkPolicy.allowDays, "day")
                                .valueOf()
                            )
                          }
                          hasTimezone
                          classes="mg ml-24 mt-8"
                          popperPlacement={"top-end"}
                          isClient={isClient}
                          minDate={new Date()}
                          maxDate={new Date(dayjs().add(999, "d").valueOf())}
                          onSubmit={(
                            date:
                              | Date
                              | {
                                  start?: Date | undefined;
                                  end?: Date | undefined;
                                }
                          ) => {
                            changeMyBoxFileShareLinkPolicy({
                              ...myBoxFileShareLinkPolicy,
                              allowDays: dayjs(
                                date instanceof Date ? date : date.start
                              )
                                .startOf("day")
                                .diff(dayjs().startOf("day"), "day"),
                            });
                          }}
                        />
                      )}
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        )}
      {["CREATE_NEW_WORKGROUP", "CREATE_NEW_SUB_WORKGROUP"].includes(
        modalType
      ) && (
        <Box classes="mg mt-20" alignItems="center ">
          <Box direction="column">
            <Typography type="subtitle2">
              {t("workgroup.createPrivate")}
            </Typography>
            <Typography
              type="body3_rg"
              exactColor={getColor("COLOR_TEXT_SECONDARY")}
              classes="mg mt-4"
              width={418}
            >
              {t("workgroup.createPrivateDesc")}
            </Typography>
          </Box>
          <Toggle
            classes="mg ml-auto"
            checked={workgroupInfo?.createWorkgroupHub}
            onChange={() =>
              handleChangeWorkgroupInfo(
                "createWorkgroupHub",
                !workgroupInfo?.createWorkgroupHub
              )
            }
          />
        </Box>
      )}
      <Box direction="column" classes="mg mt-20">
        {modalType === "SELECT_RECEIVER" ? (
          <Box classes="mg mb-6">
            <Checkbox
              label={t("common.sendMessage")}
              check={isSendShareMail}
              onClick={() => {
                changeSendShareMail(isSendShareMail ? false : true);
              }}
            />
          </Box>
        ) : (
          <Typography type="subtitle2" classes="mg mb-6">
            {t("common.message")}
          </Typography>
        )}
        {!(modalType === "SELECT_RECEIVER" && !isSendShareMail) && (
          <InputArea
            placeholder={t("common.messagePlaceholder")}
            size="sm"
            rows={3}
            maxLength={256}
            value={message}
            disabled={modalType === "SELECT_RECEIVER" && !isSendShareMail}
            onChange={(e) => {
              changeMessage && changeMessage(e.target.value);
              handleChangeWorkgroupInfo("invitedMessage", e.target.value);
            }}
          />
        )}
      </Box>
    </Box>
  );
};
export default CreateNewWorkgroupPage;
